import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { VARIABLES, readError, renderPreview, statusBadge, templateVariables } from "../../helpers/whatsappTemplates";
import { WhatsAppPreview } from "./WhatsAppPreview";

const EMPTY = {
    name: "", language: "fr", category: "MARKETING", header: "product_image", headerText: "", headerImageUrl: "",
    body: "Salut {{prenom}} ! ", footer: "The Running Collective",
    buttons: [{ kind: "product_url", text: "Voir l'offre" }, { kind: "optout", text: "Ne plus recevoir" }],
    examples: {}, status: "draft",
};

const chip = (on) => ({ display: "inline-flex", border: "1px solid " + (on ? "#111" : "#ddd"), borderRadius: 999, padding: "5px 11px", fontSize: 13,
    margin: "0 6px 6px 0", background: on ? "#111" : "#fff", color: on ? "#fff" : "#111", cursor: "pointer" });

// Éditeur de template WhatsApp : brouillon, soumission à Meta, aperçu avec les exemples.
export function TemplateEditor() {
    const { name, language } = useParams();
    const history = useHistory();
    const isNew = name === "new";
    const [t, setT] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (isNew) { setT({ ...EMPTY, language: language || "fr" }); return; }
        apiFetch(API.back + "/whatsapp-templates", { market: false })
            .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
            .then((list) => {
                const found = list.find((x) => x.name === name && x.language === language);
                if (!found) throw new Error("template introuvable");
                setT({ ...EMPTY, ...found, examples: found.examples || {}, buttons: found.buttons || [] });
            })
            .catch((e) => setError(e.message));
    }, [name, language, isNew]);

    if (error && !t) return <Alert color="danger">{error}</Alert>;
    if (!t) return <Spinner size="sm" />;

    const editable = t.status === "draft" || t.status === "rejected";
    const set = (patch) => setT((x) => ({ ...x, ...patch }));
    const vars = templateVariables(t.body);
    const insert = (v) => {
        const el = bodyRef.current;
        const pos = el ? el.selectionStart : t.body.length;
        set({ body: t.body.slice(0, pos) + `{{${v}}}` + t.body.slice(pos) });
    };
    const setButton = (i, patch) => set({ buttons: t.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)) });

    const save = async () => {
        setBusy(true); setError(null);
        const res = await apiFetch(API.back + "/whatsapp-template", { method: "PUT", market: false, body: JSON.stringify(t) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return null; }
        const saved = await res.json();
        setT({ ...t, ...saved });
        if (isNew) history.replace(`/whatsapp/template/${saved.name}/${saved.language}`);
        return saved;
    };
    const submit = async () => {
        const saved = await save();
        if (!saved) return;
        setBusy(true);
        const res = await apiFetch(API.back + `/whatsapp-template-submit?name=${encodeURIComponent(saved.name)}&language=${saved.language}`, { method: "POST", market: false });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setT({ ...t, ...(await res.json()) });
    };
    const remove = async () => {
        if (!window.confirm("Supprimer ce template ?")) return;
        const res = await apiFetch(API.back + `/whatsapp-template?name=${encodeURIComponent(t.name)}&language=${t.language}`, { method: "DELETE", market: false });
        if (!res.ok) { setError(await readError(res)); return; }
        history.push("/whatsapp/templates");
    };

    const preview = renderPreview(t.body, t.examples);
    const st = statusBadge(t.status);
    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h3 mb-0">{isNew ? "Nouveau template" : t.name}</h1>
                <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: st.bg, color: st.color }}>● {st.label}</span>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            {t.rejectedReason && <Alert color="warning">Refusé par Meta : {t.rejectedReason}. Corrige puis soumets de nouveau.</Alert>}
            <Row>
                <Col lg="7">
                    <Card><CardBody>
                        <Row>
                            <Col md="6"><FormGroup><Label>Nom (Meta)</Label>
                                <Input value={t.name} disabled={!isNew} placeholder="black_friday_produit" onChange={(e) => set({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} /></FormGroup></Col>
                            <Col md="3"><FormGroup><Label>Langue</Label>
                                <Input type="select" value={t.language} disabled={!isNew} onChange={(e) => set({ language: e.target.value })}><option value="fr">Français</option><option value="es">Espagnol</option></Input></FormGroup></Col>
                            <Col md="3"><FormGroup><Label>Catégorie</Label><Input value="Marketing" disabled /></FormGroup></Col>
                        </Row>
                        <FormGroup><Label>En-tête</Label><div>
                            {[["none", "Aucun"], ["text", "Texte"], ["image", "Image fixe"], ["product_image", "Image du produit (personnalisée)"]].map(([k, l]) =>
                                <span key={k} style={chip(t.header === k)} onClick={() => editable && set({ header: k })}>{l}</span>)}
                        </div>
                            {t.header === "text" && <Input className="mt-2" maxLength={60} value={t.headerText} disabled={!editable} onChange={(e) => set({ headerText: e.target.value })} />}
                            {t.header === "image" && <Input className="mt-2" placeholder="https://… (image de l'annonce)" value={t.headerImageUrl} disabled={!editable} onChange={(e) => set({ headerImageUrl: e.target.value })} />}
                        </FormGroup>
                        <FormGroup><Label>Message</Label>
                            <Input type="textarea" rows={5} innerRef={bodyRef} value={t.body} disabled={!editable} onChange={(e) => set({ body: e.target.value })} />
                            <div className="mt-2 small text-muted">Insérer : {VARIABLES.map((v) => <Button key={v.key} size="sm" color="link" className="p-0 mr-2" disabled={!editable} onClick={() => insert(v.key)}>{`{{${v.key}}}`}</Button>)}
                                · une variable ne peut ni ouvrir ni terminer le message</div>
                        </FormGroup>
                        {vars.length > 0 && <FormGroup><Label>Exemples soumis à Meta</Label><Row>
                            {vars.map((v) => <Col md="6" key={v} className="mb-2"><Input bsSize="sm" placeholder={v} value={t.examples[v] || ""} disabled={!editable}
                                onChange={(e) => set({ examples: { ...t.examples, [v]: e.target.value } })} /></Col>)}
                        </Row></FormGroup>}
                        <FormGroup><Label>Pied de page</Label><Input maxLength={60} value={t.footer} disabled={!editable} onChange={(e) => set({ footer: e.target.value })} /></FormGroup>
                        <FormGroup><Label>Boutons (2 maximum)</Label>
                            {t.buttons.map((b, i) => (
                                <Row key={i} className="mb-2 align-items-center">
                                    <Col md="4"><Input type="select" bsSize="sm" value={b.kind} disabled={!editable} onChange={(e) => setButton(i, { kind: e.target.value })}>
                                        <option value="product_url">Lien vers le produit du contact</option><option value="static_url">Lien fixe</option><option value="optout">Ne plus recevoir</option></Input></Col>
                                    <Col md="3"><Input bsSize="sm" maxLength={25} value={b.text} disabled={!editable} onChange={(e) => setButton(i, { text: e.target.value })} /></Col>
                                    <Col md="4">{b.kind === "static_url" && <Input bsSize="sm" placeholder="https://app.therunningcollective.fr/…" value={b.url || ""} disabled={!editable} onChange={(e) => setButton(i, { url: e.target.value })} />}</Col>
                                    <Col md="1">{editable && <Button size="sm" color="link" onClick={() => set({ buttons: t.buttons.filter((_, j) => j !== i) })}>✕</Button>}</Col>
                                </Row>
                            ))}
                            {editable && t.buttons.length < 2 && <Button size="sm" color="light" onClick={() => set({ buttons: [...t.buttons, { kind: "optout", text: "Ne plus recevoir" }] })}>+ Bouton</Button>}
                        </FormGroup>
                        <div className="d-flex justify-content-between">
                            <div>{!isNew && editable && <Button color="link" className="text-danger" onClick={remove}>Supprimer</Button>}</div>
                            {editable && <div>
                                <Button color="light" className="mr-2" disabled={busy} onClick={save}>Enregistrer le brouillon</Button>
                                <Button color="primary" disabled={busy} onClick={submit}>{busy ? <Spinner size="sm" /> : "Soumettre à Meta"}</Button>
                            </div>}
                        </div>
                    </CardBody></Card>
                </Col>
                <Col lg="5">
                    <Card><CardBody>
                        <div className="small text-muted mb-2">Aperçu avec les exemples</div>
                        <WhatsAppPreview header={t.header} headerText={t.headerText} imageURL={t.header === "image" ? t.headerImageUrl : ""} body={preview} footer={t.footer} buttons={t.buttons} />
                    </CardBody></Card>
                </Col>
            </Row>
        </Container>
    );
}
